import { useState, useCallback } from 'react';

/**
 * Custom hook to generate a session-specific form ID for workflow create/edit pages.
 * This ensures each browser tab has its own independent form data.
 * 
 * @param baseFormId - The base form schema ID (e.g., 'mfrForm', 'netppmForm')
 * @param isEditMode - Whether the page is in edit mode
 * @param editId - The workflow ID when in edit mode (optional)
 * @returns A tuple of [sessionFormId, cleanup function]
 */
export const useSessionFormId = (
  baseFormId: string,
  isEditMode: boolean,
  editId?: string
): [string, () => void] => {
  const [sessionFormId] = useState(() => {
    if (isEditMode && editId) {
      // In edit mode, use the workflow ID as the form ID (shared across tabs for same workflow)
      return `${baseFormId}_edit_${editId}`;
    } else {
      // In create mode, generate a unique session ID per tab
      const sessionKey = `${baseFormId}_create_session_id`;
      let sessionId = sessionStorage.getItem(sessionKey);
      
      if (!sessionId) {
        // Generate new session ID: timestamp + random string
        sessionId = `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
        sessionStorage.setItem(sessionKey, sessionId);
      }
      
      return `${baseFormId}_create_${sessionId}`;
    }
  });

  /**
   * Cleanup function to remove sessionStorage entry for this tab.
   * Safe to call - only affects this tab's sessionStorage, not other tabs.
   * Only cleans up create mode sessions (edit mode sessions are shared).
   */
  const cleanup = useCallback(() => {
    if (!isEditMode) {
      const sessionKey = `${baseFormId}_create_session_id`;
      sessionStorage.removeItem(sessionKey);
    }
  }, [baseFormId, isEditMode]);

  return [sessionFormId, cleanup];
};
